import React, { useState, useEffect } from 'react';
import { FILTER_IMAGES } from '../../../constants/filterImages';
import Modal from '../../../components/ui/Modal';

const MaterialsGrid = () => {
  const [materials, setMaterials] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Organizar materiais por categoria
  const organizeMaterialsByCategory = () => {
    const organizedMaterials = {
      'tecido-premium': [],
      'tecidos': [],
      'cordas': [],
      'fibras': [],
      'madeiras': [],
      'outros': []
    };

    Object.entries(FILTER_IMAGES).forEach(([name, imagePath]) => {
      // Filtrar materiais sem fotos (que usam no_image.png)
      if (imagePath === '/assets/images/no_image.png') {
        return;
      }

      const material = { name, imagePath };
      
      if (name.includes('angra') || name.includes('buzios') || name.includes('marau') || 
          name.includes('itaparica') || name.includes('trancoso') || name.includes('parati')) {
        organizedMaterials['tecido-premium'].push(material);
      } else if (name.includes('lisato') || name.includes('duna')) {
        organizedMaterials['tecidos'].push(material);
      } else if (name.includes('azul') || name.includes('preto') || name.includes('verde') || 
                 name.includes('rami') || name.includes('terracota') || name.includes('areia') || 
                 name.includes('cinza') || name.includes('bordô')) {
        organizedMaterials['cordas'].push(material);
      } else if (name.includes('verde escuro') || name.includes('camurça') || name.includes('goiaba') || 
                 name.includes('marinho') || name.includes('sepia') || name.includes('pedra ferro') || 
                 name.includes('branco') || name.includes('natural') || name.includes('feijó') || 
                 name.includes('eucalipto') || name.includes('cambara') || name.includes('cumaru') || 
                 name.includes('carvalho') || name.includes('cinza') || name.includes('chocolate')) {
        organizedMaterials['fibras'].push(material);
      } else if (name.includes('peroba') || name.includes('teca') || name.includes('ipê') || 
                 name.includes('jeguitiba') || name.includes('cumaru')) {
        organizedMaterials['madeiras'].push(material);
      } else {
        organizedMaterials['outros'].push(material);
      }
    });

    return organizedMaterials;
  };

  useEffect(() => {
    const organizedMaterials = organizeMaterialsByCategory();
    setMaterials(organizedMaterials);
  }, []);

  const getFilteredMaterials = () => {
    return Object.values(materials).flat();
  };

  const getCategoryTitle = (category) => {
    const titles = {
      'tecido-premium': 'Tecidos Premium',
      'tecidos': 'Tecidos Básicos',
      'cordas': 'Cordas Naturais',
      'fibras': 'Fibras Especiais',
      'madeiras': 'Madeiras Nobres',
      'outros': 'Outros Materiais'
    };
    return titles[category] || category;
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      'tecido-premium': 'Tecidos de alta qualidade para acabamentos sofisticados',
      'tecidos': 'Tecidos clássicos para diferentes estilos de decoração',
      'cordas': 'Cordas naturais em diversas cores para detalhes únicos',
      'fibras': 'Fibras naturais para texturas e acabamentos especiais',
      'madeiras': 'Madeiras nobres brasileiras para estruturas resistentes',
      'outros': 'Outros materiais especiais para personalização'
    };
    return descriptions[category] || '';
  };

  const handleMaterialClick = (material) => {
    setSelectedMaterial(material);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMaterial(null);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Mostrar todas as categorias */}
        {Object.entries(materials).map(([category, categoryMaterials]) => (
          categoryMaterials.length > 0 && (
            <div key={category} className="mb-16">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {getCategoryTitle(category)}
                </h3>
                <p className="text-gray-600">{getCategoryDescription(category)}</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {categoryMaterials.map((material, index) => (
                  <div
                    key={index}
                    className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
                    onClick={() => handleMaterialClick(material)}
                  >
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={material.imagePath}
                        alt={material.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = '/assets/images/no_image.png';
                        }}
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-gray-900 text-center truncate">
                        {material.name}
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>

      {/* Modal para visualização ampliada */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedMaterial?.name}
      >
        {selectedMaterial && (
          <div className="flex flex-col items-center">
            <div className="w-full max-w-2xl mb-6">
              <img
                src={selectedMaterial.imagePath}
                alt={selectedMaterial.name}
                className="w-full h-auto rounded-lg shadow-lg"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
            </div>
            <div className="text-center">
              <h4 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedMaterial.name}
              </h4>
              <p className="text-gray-600 mb-4">
                Clique fora da modal ou pressione ESC para fechar
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                  Material Premium
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  Qualidade Garantida
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default MaterialsGrid;
